import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Paginate } from '@shared/entities/paginate';
import { User } from '@shared/entities/user';
import { Roles } from '@shared/models/types';
import { ApiService, Pagination } from '@shared/services';
import { BehaviorSubject, Observable, Subject, catchError, map, of, shareReplay, switchMap, take, tap } from 'rxjs';

const LOG_PREFIX = '[Api][User] ';

export interface UserFilterURL {
  keyword?: string;
  role?: string;
  roles?: Roles[];
  status?: string;
  serialNumber?: string;
  accountId?: string;
  direction?: 'ASC' | 'DESC';
  id?: string;
  orderBy?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService extends ApiService<User> {
  private _refresh$: Subject<Pagination>;
  allUsers$: Observable<Paginate<User>>;
  private _currentPaginator: Pagination;
  private _currentFilters: UserFilterURL = {};

  private readonly PER_PAGE = 10;

  set filters(value: UserFilterURL) {
    this._currentFilters = value;
    this.refresh(1, this.PER_PAGE);
  }

  constructor(private _activatedRoute: ActivatedRoute) {
    const masterUrl = '/users';
    const detailUrl = '/users';

    super(User, masterUrl, detailUrl);

    this._currentPaginator = { page: 1, perPage: this.PER_PAGE };

    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this.allUsers$ = this._refresh$.pipe(
      switchMap((pagination) => {
        const { locationId } = this._activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }
        if (this._currentFilters.keyword !== undefined) {
          this.setUrlParameter('keyword', this._currentFilters.keyword);
        }
        if (this._currentFilters.role !== undefined) {
          this.setUrlParameter('role', this._currentFilters.role);
        }
        if (this._currentFilters.status !== undefined) {
          this.setUrlParameter('status', this._currentFilters.status);
        }
        if (this._currentFilters.serialNumber !== undefined) {
          this.setUrlParameter('serialNumber', this._currentFilters.serialNumber);
        }
        if (this._currentFilters.accountId !== undefined) {
          this.setUrlParameter('accountId', this._currentFilters.accountId);
        }
        if (this._currentFilters.roles !== undefined) {
          if (this._currentFilters.roles && this._currentFilters.roles.length > 0) {
            this.setUrlParameter('roles', this._currentFilters.roles.join(','));
          }
        }
        this.setUrlParameter('order_by', this._currentFilters.orderBy || 'createdDate');
        if (this._currentFilters.orderBy) {
          if (this._currentFilters.orderBy === 'createdDate') {
            this.setUrlParameter('direction', 'DESC');
          } else if (this._currentFilters.orderBy === 'modifiedDate') {
            this.setUrlParameter('direction', 'DESC');
          } else if (this._currentFilters.orderBy === 'email') {
            this.setUrlParameter('direction', 'ASC');
          } else {
            this.setUrlParameter('direction', 'DESC');
          }
        } else {
          this.setUrlParameter('direction', 'DESC');
        }
        return this.paginate(User, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ users', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ users error', err)
      }),
      shareReplay(1)
    );
  }

  refresh(page: number, perPage: number = this.PER_PAGE) {
    this.logger.debug(LOG_PREFIX + 'refresh users');
    this._currentPaginator = { page, perPage };
    this._refresh$.next(this._currentPaginator);
    return this.allUsers$.pipe(take(1));
  }

  refreshFilters() {
    this.logger.debug(LOG_PREFIX + 'refresh filters');
    this._currentFilters = {};
    this.refresh(1, this.PER_PAGE);
  }

  getAllUsers(): Observable<User[]> {
    this.logger.debug(LOG_PREFIX + 'getAllUsers');
    return this.paginate(User, 1, 200).pipe(
      map((paginate) => paginate.data || []),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'getAllUsers error', error);
        return of([]);
      })
    );
  }
}
